/* -----------------Globals--------------- */
import { useMemo } from "react";

/* -----------------Child components--------------- */
import Box from "../Box";
import MessageComponent from "../Message/ChatMessage";
import UserMessage from "../Message/UserMessage";
import type { IconType } from "../TextLoop";
/* -----------------Types--------------- */
import type {
	AiAssistantInfo,
	GetMessageActions,
	ToolInfo,
	VectorDocument,
} from "./types";

type Props = {
	assistantInfo: AiAssistantInfo;
	type: "human" | "assistant";
	content: string;
	loading?: boolean;
	streaming?: boolean;
	references?: VectorDocument[] | string;
	loader?: React.ReactNode;
	handleNavigateToReference?: (url: string) => void;
	showReference?: boolean;
	messageActions?: GetMessageActions;
	referenceThumbnails?: Record<string, string>;
	runId?: string;
	toolInfo?: ToolInfo | null;
	textToSpeach: (runId: string, content: string) => React.ReactNode;
	errorMessage?: string;
	onPressRetry?: () => void;
	isLastMessage?: boolean;
	isFirstMessage?: boolean;
};

const getMessageState = (
	loading?: boolean,
	streaming?: boolean,
	toolInfo?: ToolInfo | null,
	errorMessage?: string,
	onPressRetry?: () => void,
) => {
	if (errorMessage && onPressRetry) {
		return "error";
	}
	if (toolInfo) {
		return "toolCalling";
	}
	if (loading) {
		return "loading";
	}

	if (streaming) {
		return "streaming";
	}
	return "completed";
};

const createMessageActions = (
	messageActionsList: ReturnType<GetMessageActions> | undefined,
	content: string,
	onPressRetry?: () => void,
) => {
	if (!messageActionsList) {
		return { leadingIcons: undefined, trailingIcon: undefined };
	}

	const leadingIcons = messageActionsList
		.filter(
			(action) =>
				action.icon !== "thumb-up-outline" &&
				action.icon !== "thumb-down-outline" &&
				action.icon !== "flag-outline",
		)
		.map((action) => ({
			title: action.tooltip,
			icon: action.icon as IconType,
			onPress: () => action.handleOnPress(content),
		}));

	const trailingIcon = {
		icon: "dots-vertical" as IconType,
		onResponseThumbUp: () =>
			messageActionsList
				?.find((action) => action.icon === "thumb-up-outline")
				?.handleOnPress(content),
		onResponseThumbDown: () =>
			messageActionsList
				?.find((action) => action.icon === "thumb-down-outline")
				?.handleOnPress(content),
		onReportIssue: () =>
			messageActionsList
				?.find((action) => action.icon === "flag-outline")
				?.handleOnPress(content),
		onRegenerateResponse: () => onPressRetry?.(),
	};

	return { leadingIcons, trailingIcon };
};

const shouldShowActions = (state: string, toolInfo?: ToolInfo | null) => {
	return (
		state === "completed" ||
		(state === "toolCalling" && toolInfo?.status !== "running")
	);
};

export const ChatMessage = ({
	type,
	content,
	loading,
	streaming,
	messageActions,
	assistantInfo,
	runId,
	toolInfo,
	textToSpeach,
	errorMessage,
	isLastMessage,
	isFirstMessage,
	onPressRetry,
}: Props) => {
	const messageActionsList = useMemo(
		() => messageActions?.(undefined, runId),
		[messageActions, runId],
	);

	if (type === "human") {
		return (
			<Box>
				<UserMessage
					text={content}
					type="text"
					onCopy={() =>
						messageActionsList
							?.find((action) => action.icon === "content-copy")
							?.handleOnPress(content)
					}
				/>
			</Box>
		);
	}

	const messageState = getMessageState(
		loading,
		streaming,
		toolInfo,
		errorMessage,
		onPressRetry,
	);
	const { leadingIcons, trailingIcon } = createMessageActions(
		messageActionsList,
		content,
		onPressRetry,
	);
	const showActions = shouldShowActions(messageState, toolInfo);

	const baseProps = {
		aiName: assistantInfo.alias,
		aiAvatar: assistantInfo.icon,
		textToSpeach:
			messageState === "streaming" || toolInfo?.status === "running"
				? undefined
				: textToSpeach(runId ?? "", content),
		leadingIcons: showActions ? leadingIcons : undefined,
		trailingIcon: showActions ? trailingIcon : undefined,
		isLastMessage,
		isFirstMessage,
	};

	switch (messageState) {
		case "error":
			return (
				<MessageComponent
					{...baseProps}
					type={{
						type: "networkError",
						networkErrorMessage: errorMessage!,
						onPressRetry: onPressRetry!,
					}}
				/>
			);

		case "loading":
			return (
				<MessageComponent
					{...baseProps}
					type={{
						type: "processing",
					}}
				/>
			);

		case "toolCalling":
			return (
				<Box>
					<MessageComponent
						{...baseProps}
						type={{
							type: "toolCalling",
							text: toolInfo!.title,
							toolCallingIcon:
								toolInfo!.status === "completed"
									? "check"
									: (toolInfo!.icon as IconType),
							content: content,
							toolCallingState: toolInfo!.status,
						}}
					/>
				</Box>
			);

		case "streaming":
			return (
				<MessageComponent
					{...baseProps}
					type={{
						type: "streaming",
						text: content,
					}}
				/>
			);

		default:
			return (
				<MessageComponent
					{...baseProps}
					type={{
						type: "streamed",
						text: content,
					}}
				/>
			);
	}
};
